import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { scrubForPii } from 'owa-config';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { makePostRequest } from 'owa-ows-gateway';
import { createExponentialBackoffFunction, createRetriableFunction } from 'owa-retriable-function';
import { getUserConfiguration } from 'owa-session-store';
import type { AdminRbacQueryInput, AdminRbacQueryResponse } from '../interfaces';

// Known message patterns to redact names from (e.g. The user "Name" isn't assigned ...)
const userNameInMessageRegex = /The user\s+"([^"]+)"\s+isn'?t assigned to any management roles\./i;

function redactKnownPatterns(message: string | undefined | null): string | undefined {
    if (!message) {
        return '';
    }
    if (userNameInMessageRegex.test(message)) {
        return message.replace(
            userNameInMessageRegex,
            'The user "UserPii" isn\'t assigned to any management roles.'
        );
    }
    return message;
}

type ErrorWithStatus = Error & {
    status: number;
};

export async function queryAdminRbac(
    inputs: AdminRbacQueryInput[],
    attemptCount: number = 0
): Promise<Partial<Record<AdminRbacQueryInput, boolean>>> {
    const perfDatapoint = new PerformanceDatapoint(
        'PlacesManagementPermissionCheck',
        getModuleContextMailboxInfo()
    );
    perfDatapoint.addCustomData({
        attemptCount,
    });
    try {
        const tenantId = getUserConfiguration().SessionSettings?.ExternalDirectoryTenantGuid;
        const response = await makePostRequest(
            `/adminapi/beta/${tenantId}/RbacQuery`,
            {
                QueryTable: {
                    QueryStrings: inputs,
                },
            },
            undefined,
            true,
            null,
            undefined,
            undefined,
            undefined,
            'AdminRbacQuery',
            undefined,
            getModuleContextMailboxInfo()
        );

        perfDatapoint.addCustomData({
            responseStatus: response.status,
            responseStatusText: response.statusText,
            isSuccessStatus: isSuccessStatusCode(response.status),
        });

        if (isSuccessStatusCode(response.status)) {
            let results: boolean[] = [];
            try {
                const responseText = await response.text();
                const contentType = response.headers.get('content-type');

                perfDatapoint.addCustomData({
                    contentType,
                    responseLength: responseText.length,
                });

                const isEmptyResponse = !responseText || responseText.trim().length === 0;
                const isOkResponse = responseText.trim().toLowerCase() === 'ok';

                if (isEmptyResponse || isOkResponse) {
                    const baseError: ErrorWithStatus = {
                        name: 'RbacQueryError',
                        message: `Received unexpected response from RbacQuery API: ${
                            isEmptyResponse ? 'empty body' : 'plain text "OK"'
                        }`,
                        status: 598, // Custom status for retriable invalid response format
                    };

                    perfDatapoint.addCustomData({
                        isEmptyResponse,
                        isOkResponse,
                    });

                    throw baseError;
                }

                const responseData = JSON.parse(responseText) as AdminRbacQueryResponse;
                results = responseData?.Results || [];

                perfDatapoint.addCustomData({
                    resultsCount: results.length,
                });
            } catch (parseError) {
                // If it's already our custom RbacQueryError, re-throw it
                // Errors resulting from enabling useUnknownInCatchVariables
                // TS18046: 'parseError' is of type 'unknown'.
                // @ts-expect-error
                if (parseError.name === 'RbacQueryError') {
                    throw parseError;
                }

                // Enhanced error handling for other JSON parsing issues
                const contentType = response.headers.get('content-type');
                const baseError: ErrorWithStatus = {
                    name: 'RbacQueryError',
                    message: `Failed to parse JSON response from RbacQuery API. Content-Type: ${contentType}, Error: ${scrubForPii(
                        // Errors resulting from enabling useUnknownInCatchVariables
                        // TS18046: 'parseError' is of type 'unknown'.
                        // @ts-expect-error
                        redactKnownPatterns(parseError.message)
                    )}`,
                    status: response.status,
                };

                perfDatapoint.addCustomData({
                    contentType,
                    // Errors resulting from enabling useUnknownInCatchVariables
                    // TS18046: 'parseError' is of type 'unknown'.
                    // @ts-expect-error
                    parseError: parseError.message,
                });

                throw baseError;
            }

            const records: Partial<Record<AdminRbacQueryInput, boolean>> = {};
            for (let i = 0; i < inputs.length; i++) {
                records[inputs[i]] = results[i];
            }

            perfDatapoint.end();
            return records;
        } else {
            let responseError: any;
            let responseText: string;
            try {
                responseText = await response.text();
                const contentType = response.headers.get('content-type');

                perfDatapoint.addCustomData({
                    errorResponseLength: responseText.length,
                    errorContentType: contentType,
                });

                // Try to parse as JSON if it looks like JSON
                if (responseText.trim().startsWith('{') || responseText.trim().startsWith('[')) {
                    responseError = scrubForPii(redactKnownPatterns(JSON.parse(responseText)));
                } else {
                    // Handle plain text error responses
                    responseError = { message: scrubForPii(redactKnownPatterns(responseText)) };
                }
            } catch (parseError) {
                const baseError: ErrorWithStatus = {
                    name: 'RbacQueryError',
                    message:
                        scrubForPii(redactKnownPatterns(response.statusText)) ||
                        'Failed to check permission with RbacQuery API, the response is not parseable.',
                    status: response.status,
                };

                perfDatapoint.addCustomData({
                    // Errors resulting from enabling useUnknownInCatchVariables
                    // TS18046: 'parseError' is of type 'unknown'.
                    // @ts-expect-error
                    errorParseError: scrubForPii(redactKnownPatterns(parseError.message)),
                    errorResponseParseFailure: true,
                });

                throw baseError;
            }

            const errorObj: ErrorWithStatus = {
                name: 'RbacQueryError',
                message: 'Failed to check permission with RbacQuery API',
                status: response.status,
                ...(responseError?.error || responseError || {}),
            };
            throw errorObj;
        }
    } catch (error) {
        let datapointStatus = DatapointStatus.ClientError;

        // If we have a numeric HTTP status, use it to determine server vs client
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS2339: Property 'status' does not exist on type '{}'.
        // @ts-expect-error
        if (typeof error?.status === 'number') {
            // Errors resulting from enabling useUnknownInCatchVariables
            // TS2339: Property 'status' does not exist on type '{}'.
            // @ts-expect-error
            const statusCode = error.status;
            // Treat 5xx and custom 598 as server errors; 4xx as client errors
            if ((statusCode >= 500 && statusCode < 600) || statusCode === 598) {
                datapointStatus = DatapointStatus.ServerError;
            } else {
                datapointStatus = DatapointStatus.ClientError;
            }
        } else {
            datapointStatus = DatapointStatus.ClientError;
        }

        perfDatapoint.addCustomData({
            // Errors resulting from enabling useUnknownInCatchVariables
            // TS2339: Property 'message' does not exist on type '{}'.
            // @ts-expect-error
            error: scrubForPii(redactKnownPatterns(error?.message)),
            // Errors resulting from enabling useUnknownInCatchVariables
            // TS2339: Property 'status' does not exist on type '{}'.
            // @ts-expect-error
            errorStatus: error?.status,
            // Errors resulting from enabling useUnknownInCatchVariables
            // TS2339: Property 'code' does not exist on type '{}'.
            // @ts-expect-error
            errorCode: error?.code,
            errorType: datapointStatus === DatapointStatus.ServerError ? 'server' : 'client',
        });
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'TraceErrorObject | ErrorCodes'.
        // @ts-expect-error
        perfDatapoint.endWithError(datapointStatus, error);
        throw error;
    }
}

export async function queryAdminRbacWithRetry(
    inputs: AdminRbacQueryInput[]
): Promise<Partial<Record<AdminRbacQueryInput, boolean>>> {
    const createRetriableFunc = createRetriableFunction({
        maximumAttempts: 3,
        timeBetweenRetryInMS: createExponentialBackoffFunction(1000),
    });
    let cancelRetry: (() => void) | null = null;
    let attemptCount = 0;
    const { retriableFunc, cancel } = createRetriableFunc(async () => {
        ++attemptCount;
        try {
            const result = await queryAdminRbac(inputs, attemptCount);
            return result;
        } catch (error) {
            const errorStatus = (error as ErrorWithStatus)?.status;
            const isRetriable = retriableStatusCodes.includes(errorStatus);

            if (!isRetriable) {
                cancelRetry?.(); // Do not retry on permanent errors
            }
            throw error;
        }
    });
    cancelRetry = cancel;
    const response = await retriableFunc();
    return response;
}

const retriableStatusCodes = [408, 429, 500, 502, 503, 504, 598]; // 598 = custom retriable invalid response format
